import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { BarChart3 } from 'lucide-react';
import { get, ApiError } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { KpiTile } from '@/components/domain/KpiTile';
import { Skeleton } from '@/components/ui/skeleton';
import { money, monthLabel, recentMonths, reportHref, type BillingReport } from '@/lib/billing';
import { dollars } from '@/lib/payouts';
import type { OrderStats } from '@/components/domain/StoreEditorDrawer';

type StoreCounts = {
  live?: number | null;
  totalKnown?: number;
  byStatus?: Record<string, number | null>;
};

type HomePayload = {
  kpis?: Record<string, { label: string; value: number | null }>;
  stores?: StoreCounts;
};

type LiveStoreRow = {
  id: string;
  orderStats?: OrderStats;
};

function givebackAmount(stats?: OrderStats): number {
  if (!stats) return 0;
  if (stats.raised_amount != null && Number.isFinite(Number(stats.raised_amount))) {
    return Number(stats.raised_amount);
  }
  return Number(stats.totalCommission) || 0;
}

const STATUS_LABEL: Record<string, string> = {
  live: 'Live',
  pending_review: 'Pending review',
  creating_products: 'Building',
  live_with_errors: 'Live w/ errors',
  closing_soon: 'Ends soon',
  shopify_error: 'Shopify error',
};

export default function Reports() {
  const month = recentMonths(1)[0];
  const { data: home, isLoading: homeLoading } = useSWR<HomePayload>('/api/reports/home', get);
  const { data: billing, error: billingError, isLoading: billingLoading } = useSWR<BillingReport>(
    reportHref(month),
    get,
    { shouldRetryOnError: false },
  );
  const { data: liveStores, isLoading: financesLoading } = useSWR<{ stores?: LiveStoreRow[] }>(
    '/api/active-stores?status=live,live_with_errors&limit=200',
    get,
    { shouldRetryOnError: false },
  );
  const financeRows = liveStores?.stores || [];
  const finance = financeRows.reduce(
    (acc, s) => {
      const stats = s.orderStats || {};
      acc.revenue += Number(stats.totalRevenue) || 0;
      acc.giveback += givebackAmount(stats);
      acc.rep += Number(stats.totalSalesRepCommission) || 0;
      if (stats.totalRevenue != null || stats.raised_amount != null || stats.totalCommission != null) {
        acc.withStats += 1;
      }
      return acc;
    },
    { revenue: 0, giveback: 0, rep: 0, withStats: 0 },
  );

  const stores = home?.stores || {};
  const byStatus = stores.byStatus || {};
  const billingBlocked = billingError instanceof ApiError && billingError.status === 503;

  return (
    <div className="px-7 py-6">
      <PageHeader
        title="Reports"
        subtitle="Store counts, a thin store-revenue vs giveback vs rep shell, and the reports we already run. Not a company P&L."
      />

      <section className="mb-6">
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Stores</h2>
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <KpiTile label="Live" value={stores.live ?? home?.kpis?.stores.value ?? undefined} loading={homeLoading} />
          <KpiTile label="Pending review" value={byStatus.pending_review ?? undefined} loading={homeLoading} />
          <KpiTile label="Building" value={byStatus.creating_products ?? undefined} loading={homeLoading} />
          <KpiTile label="Known statuses" value={stores.totalKnown ?? undefined} loading={homeLoading} />
        </div>
        <div className="overflow-hidden rounded-[12px] border border-black/[0.05] bg-surface shadow-card">
          {homeLoading ? (
            <div className="space-y-2 p-3"><Skeleton className="h-6 w-full" /><Skeleton className="h-6 w-2/3" /></div>
          ) : (
            Object.entries(STATUS_LABEL).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between border-b border-divider px-3.5 py-2 last:border-b-0">
                <span className="text-[12.5px]">{label}</span>
                <span className="tabular-nums text-[12.5px]">{byStatus[key] ?? '—'}</span>
              </div>
            ))
          )}
        </div>
        <p className="mt-2 text-[11px] text-muted">
          Counts are Firestore status totals, not revenue. Open a store from{' '}
          <Link to="/active-stores" className="text-primary underline-offset-2 hover:underline">Active Stores</Link>.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">
          Personalization billing · {monthLabel(month)}
        </h2>
        {billingLoading ? (
          <Skeleton className="h-24 w-full rounded-[14px]" />
        ) : billingBlocked ? (
          <p className="rounded-[14px] border border-dashed border-border p-6 text-[12px] text-muted">
            Billing index is not ready yet.{' '}
            <Link to="/personalization/billing" className="text-primary underline-offset-2 hover:underline">
              Open the billing report
            </Link>{' '}
            for the create-index link.
          </p>
        ) : billing ? (
          <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
            <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <KpiTile label="Fundraisers" value={billing.fundraiserCount} />
              <KpiTile label="Orders" value={billing.orderCount} />
              <KpiTile label="Decorated units" value={billing.totals.personalizedUnits} />
              <div className="rounded-[10px] bg-bg px-3 py-2">
                <div className="text-[11px] text-muted">Charged</div>
                <div className="text-[16px] font-semibold tracking-tight">{money(billing.totals.chargeCents)}</div>
              </div>
            </div>
            <Link to="/personalization/billing" className="text-[11.5px] text-primary underline-offset-2 hover:underline">
              Open full personalization billing →
            </Link>
          </div>
        ) : (
          <p className="rounded-[14px] border border-dashed border-border p-6 text-[12px] text-muted">
            Billing is not available right now.{' '}
            <Link to="/personalization/billing" className="text-primary underline-offset-2 hover:underline">Open billing</Link>
          </p>
        )}
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Finances</h2>
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <KpiTile label="Store revenue" value={financesLoading ? undefined : dollars(finance.revenue)} loading={financesLoading} />
          <KpiTile label="Giveback (10%)" value={financesLoading ? undefined : dollars(finance.giveback)} loading={financesLoading} />
          <KpiTile label="Rep (7%)" value={financesLoading ? undefined : dollars(finance.rep)} loading={financesLoading} />
          <KpiTile label="Stores counted" value={financesLoading ? undefined : financeRows.length} loading={financesLoading} />
        </div>
        <p className="text-[11px] text-muted">
          Lifetime <code className="font-mono">orderStats</code> on up to 200 live stores
          {finance.withStats ? ` (${finance.withStats} have numbers)` : ''}.
          Giveback uses <code className="font-mono">raised_amount</code> when present, otherwise team commission.
          Quarterly statements (10% / 7% of that quarter) live on{' '}
          <Link to="/payouts" className="text-primary underline-offset-2 hover:underline">Payouts</Link>.
          This is not a company P&L.
        </p>
      </section>

      <PeriodSection />
    </div>
  );
}

type PeriodReport = {
  range: string;
  label: string;
  orders: { count: number; units: number; revenue: number; giveback: number; rep: number };
  payouts: { count: number; revenue: number; team: number; rep: number };
  // Set when the server hit its page limit, so the window may be clipped.
  truncated?: { orders: boolean; payouts: boolean; stores: boolean };
};

type CoachKitOrders = {
  days: number;
  scanned: number;
  truncated?: boolean;
  orders: { name: string; subtotal: number }[];
  byStore: { store: string; orders: number; subtotal: number }[];
  byContent: { content: string; orders: number; subtotal: number }[];
};

// Orders that came through a coach share kit link or QR code. Read from Shopify
// on request. An error stays an error: "no tagged orders" and "could not read
// Shopify" must not look alike.
function CoachKitOrdersCard() {
  const { data, error, isLoading } = useSWR<CoachKitOrders>('/api/reports/coach-kit-orders?days=30', get, {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
  });
  return (
    <div className="rounded-[14px] border border-black/[0.05] bg-surface p-4 shadow-card">
      <div className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold tracking-tight">
        <BarChart3 className="h-3.5 w-3.5 text-subtle" aria-hidden /> Coach kit orders
      </div>
      {error ? (
        <p className="py-4 text-center text-[11.5px] text-danger">Could not read Shopify orders. These figures are unknown, not zero.</p>
      ) : isLoading || !data ? (
        <p className="py-4 text-center text-[11.5px] text-muted">Reading the last 30 days of orders…</p>
      ) : (
        <>
          <p className="mb-3 text-[11px] text-muted">
            {data.orders.length} of {data.scanned} orders in the last {data.days} days came through a kit link.
          </p>
          {data.byContent.map((r) => (
            <div key={r.content} className="text-[12px]">
              {r.content} <span className="float-right tabular-nums">{r.orders} · {dollars(r.subtotal)}</span>
            </div>
          ))}
          {data.byStore.length > 0 && <div className="my-2 border-t border-black/[0.05]" />}
          {data.byStore.map((r) => (
            <div key={r.store} className="text-[12px]">
              {r.store} <span className="float-right tabular-nums">{r.orders} · {dollars(r.subtotal)}</span>
            </div>
          ))}
          {data.truncated && <p className="mt-2 text-[11px] text-warning">Order page limit reached. This window may be incomplete.</p>}
        </>
      )}
    </div>
  );
}

function PeriodCard({ range, title }: { range: 'daily' | 'weekly' | 'monthly'; title: string }) {
  const { data, error, isLoading } = useSWR<PeriodReport>(`/api/reports/period?range=${range}`, get, {
    shouldRetryOnError: false,
  });
  const o = data?.orders;
  return (
    <div className="rounded-[14px] border border-black/[0.05] bg-surface p-4 shadow-card">
      <div className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold tracking-tight">
        <BarChart3 className="h-3.5 w-3.5 text-subtle" aria-hidden /> {title}
      </div>
      {/* The route 502s rather than returning an all-zero report, and this card
          must not undo that: "$0.00 today" and "we could not read the orders"
          look identical on a revenue tile, and only one of them is a quiet day. */}
      {error ? (
        <p className="py-4 text-center text-[11.5px] text-danger">
          Could not load {title.toLowerCase()}. These figures are unknown, not zero.
        </p>
      ) : (
        <>
          <p className="mb-3 text-[11px] text-muted">{isLoading ? '…' : data?.label || title}</p>
          <div className="grid grid-cols-2 gap-2 text-[12px]">
            <div>Orders <span className="float-right tabular-nums">{isLoading ? '—' : o?.count ?? 0}</span></div>
            <div>Revenue <span className="float-right tabular-nums">{isLoading ? '—' : dollars(o?.revenue)}</span></div>
            <div>Giveback <span className="float-right tabular-nums">{isLoading ? '—' : dollars(o?.giveback)}</span></div>
            <div>Rep <span className="float-right tabular-nums">{isLoading ? '—' : dollars(o?.rep)}</span></div>
          </div>
          {data?.payouts && data.payouts.count > 0 && (
            <p className="mt-2 text-[11px] text-muted">
              {data.payouts.count} payout{data.payouts.count === 1 ? '' : 's'} · team {dollars(data.payouts.team)} · rep {dollars(data.payouts.rep)}
            </p>
          )}
          {data?.truncated?.orders && (
            <p className="mt-2 text-[11px] text-warning">
              Order page limit reached — this window may be incomplete.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function PeriodSection() {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Daily / weekly / monthly</h2>
      <div className="grid gap-3 sm:grid-cols-3">
        <PeriodCard range="daily" title="Daily" />
        <PeriodCard range="weekly" title="Weekly" />
        <PeriodCard range="monthly" title="Monthly" />
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <CoachKitOrdersCard />
      </div>
      <p className="mt-3 text-[11px] text-muted">
        America/Chicago windows over teamStoreOrders (up to 500 recent) plus payouts in the same window.
        Lifetime tiles above use orderStats. Not a company P&L.{' '}
        <Link to="/payouts" className="text-primary underline-offset-2 hover:underline">Quarterly statements</Link>.
      </p>
    </section>
  );
}
